import { useFlyout } from "../flyout";
import { useDelayedOpen } from "../flyout/hooks/useDelayedOpen";
import { useFocusMode } from "../focusMode";
import { tooltipFlyoutDefaults } from "./tooltipConstants";
/**
 * Provides a convenience wrapper over the `useFlyout` hook with reasonable
 * defaults for a Tooltip.
 *
 * If `isOpen` is provided, we'll use it. Otherwise, set up some functionality to manage it internally.
 * This is an example of controlled/uncontrolled behavior that usually we try and stay away
 * from in lodestar-core, but Tooltip is an example of where the most common use-case will be
 * uncontrolled. We still want to provide our users with an escape hatch.
 */
export const useTooltip = function () {
  let {
    onMouseEnter,
    onMouseLeave,
    onFocus: providedOnFocus,
    onBlur,
    enterDelay = 500,
    resetDelay = 500,
    delayMode = "batch",
    showWhenTargetFocusedBy = "keyboard",
    visualOnly = false,
    closeOnScroll,
    ...rest
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    isOpen,
    setIsOpen,
    cancelEnterTimer,
    getDelayedOpenTargetProps
  } = useDelayedOpen({
    enterDelay,
    resetDelay,
    delayMode
  });

  // Originally, When focus moved to the tooltip's target we didn't want to show the Tooltip because there's
  // no indication that you're focused on anything (no outline). This has since been opened up to allow users
  // to control this behavior via `showWhenTargetFocusedBy`. If `keyboard`, the original behavior remains. If
  // `mouse`, the Tooltip will show when the target is hovered over. If `both`, the Tooltip will show when the
  // target is hovered over or focused via keyboard.
  const {
    getFocusMode
  } = useFocusMode({
    passive: true
  });
  const onFocus = e => {
    switch (showWhenTargetFocusedBy) {
      case "mouse":
      case "keyboard":
        // Hack for now.  MenuItems are "focused" during onMouseEnter.  Let their pending opens continue.
        if (getFocusMode() !== showWhenTargetFocusedBy && e.target?.getAttribute("role") !== "menuitem") {
          // Close the Tooltip if it's open already.
          setIsOpen(false);
          // After any timers are set by useDelayedOpen, cancel them so the Tooltip doesn't reopen
          // after the usual timer started in useDelayedOpen.onFocus().
          requestAnimationFrame(() => {
            cancelEnterTimer?.();
          });
        }
        break;
      case "both":
        break;
    }
    providedOnFocus?.(e);
  };
  const flyoutDefaults = closeOnScroll ? {
    ...tooltipFlyoutDefaults,
    closeOnScroll: true,
    onCloseRequested: () => {
      setIsOpen(false);
      return undefined;
    }
  } : {
    ...tooltipFlyoutDefaults
  };
  const {
    flyoutProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    targetProps: {
      "aria-owns": _0,
      ...restTargetProps
    }
  } = useFlyout({
    // These defaults are also specified in the Tooltip component, but repeated here in case
    // someone wants to manually set up a Tooltip-like Flyout.
    ...flyoutDefaults,
    ...rest,
    isOpen
  });
  return {
    tooltipProps: {
      ...flyoutProps,
      cancelEnterTimer
    },
    targetProps: {
      ...restTargetProps,
      ...(flyoutProps.isOpen && !visualOnly ? {
        "aria-describedby": flyoutProps.id
      } : {}),
      ...getDelayedOpenTargetProps({
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur
      })
    }
  };
};
//# sourceMappingURL=useTooltip.js.map